import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import ErrorNote from './ErrorNote'
import {
  FEED_KEY,
  fetchFeedActivity,
  lastHeard,
  outcomeTone,
  outcomeWords,
  payloadState,
  readableBody,
  type FeedDelivery,
  type FeedTrigger,
} from '../api/feed'

/**
 * What Jackrabbit has told us, as it happens (D-816, D-819).
 *
 * **Some of these changed the studio and most did not, so the screen says
 * which.** ⚠ It read *"Nothing here has changed anything"* while that was true
 * of every row (D-816); D-819 made two triggers move a roster, and a screen
 * still carrying that sentence would be telling Tonya the opposite of what
 * happened to her classes. The chip is the answer and the words are beside it —
 * `Applied` against `Matched` is the distinction the whole screen turns on.
 *
 * **The raw body is the point.** Everything above it is our reading of the
 * delivery; the body is the delivery. Nobody had seen what Jackrabbit really
 * sends until this shipped, so a screen that only showed our interpretation
 * would answer a question nobody asked.
 */
export default function FeedActivityView() {
  /*
   * <b>This screen keeps itself fresh, and it overrides two global defaults to
   * do it</b> (D-823). The workflow it was built for is: fire a Zap in Zapier,
   * come back here, read the row. On a list of deliveries a stale read is merely
   * unhelpful; under a panel that says <i>Never</i> it is a confident false
   * statement that invites rebuilding a Zap which works.
   *
   * <b>`'always'`, not `true`</b> — and that distinction is the whole fix.
   * `refetchOnWindowFocus: true` is *gated on staleness*: with the app-wide
   * `staleTime: 30_000` (`main.tsx`), coming back inside thirty seconds refetched
   * nothing, which is precisely the window right after firing a Zap. A first cut
   * used `true` and told the reader to switch away and back — instructing an
   * action that silently did nothing exactly when they would do it. An audit
   * read the library to find that.
   *
   * <b>And an interval, because a tab switch is not the only shape of this.</b>
   * React Query v5 listens to `visibilitychange` alone, so Zapier open in a
   * *separate window* — the natural setup on two monitors — never fires focus at
   * all. `refetchIntervalInBackground` stays false by default, so this polls only
   * while the screen is actually visible: nothing runs while he is in Zapier, and
   * the reading is current within seconds of coming back. One aggregate query on
   * an owner-only screen is a price worth paying for a panel whose entire job is
   * to be true right now.
   */
  const list = useQuery({
    queryKey: FEED_KEY,
    queryFn: fetchFeedActivity,
    refetchOnWindowFocus: 'always',
    refetchInterval: 15_000,
  })

  const rows = list.data?.deliveries ?? []

  return (
    <div className="page-shell px-5 py-8">
      <h1 className="font-display text-2xl text-ink">What Jackrabbit has sent</h1>

      <p className="mt-2 max-w-[60ch] text-[13.5px] text-ink-2">
        The 200 most recent changes Jackrabbit has told us about, newest first.
      </p>

      {/*
        <b>Said before the list rather than after it</b> (D-209). A list of drops
        that changed nothing looks exactly like a list of drops that did, so the
        reader needs to know which chip means which before reading any row.
      */}
      <p className="mt-1 max-w-[60ch] text-[12.5px] text-ink-3">
        <strong className="text-ink-2">Applied</strong> means a dancer was put on or taken off a
        roster here. Everything else was recorded and changed nothing.
      </p>

      {/*
        <b>`?? []` like its sibling two lines up.</b> A new bundle against an API
        started before this shipped sends no `triggers` at all — the ordinary
        state of a dev machine with a stale `dotnet run` — and dereferencing it
        blanked the whole screen rather than degrading to the list.
      */}
      {list.isSuccess && <Triggers rows={list.data.triggers ?? []} fetchedAt={list.dataUpdatedAt} />}

      {list.isPending && (
        <p className="mt-6 text-[13px] text-ink-2" role="status">
          Loading&hellip;
        </p>
      )}

      {list.isError && (
        <div className="mt-6">
          <ErrorNote boxed>This could not be loaded. Try again in a moment.</ErrorNote>
        </div>
      )}

      {/*
        <b>Nothing yet and nothing wrong are different sentences</b> (D-209).
        Until a Zap is pointed at this and a secret is set, an empty list is the
        correct state and reads as a fault unless it says otherwise.
      */}
      {list.isSuccess && rows.length === 0 && (
        <p className="mt-6 max-w-[60ch] text-[13.5px] text-ink-2">
          Nothing has arrived yet. That is what this looks like before a Zap is pointed at the
          studio &mdash; it does not mean anything is broken.
        </p>
      )}

      {rows.length > 0 && (
        <ul className="mt-6 flex flex-col gap-2">
          {rows.map((row) => (
            <Delivery key={row.id} row={row} />
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * **Which Zaps have ever spoken** (D-823).
 *
 * The question while ten Zaps are being built one at a time is *did that one
 * work* — and the answer is an absence, which the list below can never show. A
 * trigger saying **Never** is either a Zap not built yet or a Zap that does not
 * work, and both are things to know while the Zapier tab is still open.
 *
 * **It reports the time and does not judge it.** Out of season a silent feed is
 * the correct answer, so a threshold here would cry wolf all summer — which is
 * the same reason `Superseded` is grey. What settles *dead* against *quiet*
 * outright is a heartbeat Zap, and that costs tasks.
 */
function Triggers({ rows, fetchedAt }: { rows: FeedTrigger[]; fetchedAt: number }) {
  if (rows.length === 0) return null

  return (
    <section className="mt-6 rounded-2xl border border-line bg-surface px-4 py-3">
      <h2 className="text-[13px] font-semibold text-ink">Which Zaps have sent anything</h2>

      {/*
        <b>Three causes for Never, not two — and the third is the one the rest of
        this feature insists on</b> (D-823). Everything else written about this
        panel says it *reports the time and does not judge it*, because out of
        season a silent feed is the correct answer. The screen was the only place
        judging, and it judged wrongly: in November, with no drops for three
        weeks, it told her a working Zap was broken. An audit caught the
        contradiction between this sentence and four other artifacts, which is
        the third time on this screen that one confident sentence was false.

        <b>The counts carry the distinction the words cannot.</b> A trigger that
        has sent before and is quiet now is a different row from one that has
        never sent, and the number says which.
      */}
      <p className="mt-1 max-w-[60ch] text-[12px] text-ink-3">
        One row per trigger, with how many deliveries it has ever sent &mdash; more than the 200
        listed below, if it has.{' '}
        <strong className="text-ink-2">Never</strong> means nothing has arrived under that name as of{' '}
        {new Date(fetchedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
        : the Zap may not be built, or not working, or nothing may have happened in Jackrabbit that
        would fire it. This refreshes itself while the screen is open.
      </p>

      <ul className="mt-3 flex flex-col gap-1.5">
        {rows.map((row) => (
          <li key={row.type} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <span className="min-w-0 text-[12.5px] wrap-anywhere text-ink-2">
              {/* Machine-shaped, so it wraps anywhere rather than widening the row. */}
              <span className={row.recognised ? '' : 'text-warn'}>{row.type}</span>

              {row.acts && <span className="ml-2 text-[11px] text-ink-3">changes a roster</span>}

              {!row.recognised && (
                <span className="ml-2 text-[11px] text-warn">not a trigger we know &mdash; check the Zap</span>
              )}
            </span>

            {/*
              <b>`ml-auto` rather than leaning on `justify-between`</b>: a long
              unrecognised name wraps this to its own line at phone width, and
              space-between does nothing to a single item — so it would sit left
              on wrapped rows and right on every other one.
            */}
            <span
              className={`ml-auto shrink-0 text-[11.5px] tabular-nums ${
                row.lastReceivedAt ? 'text-ink-2' : 'text-ink-3'
              }`}
            >
              {lastHeard(row.lastReceivedAt)}
              {row.count > 0 ? ` · ${row.count} sent` : ''}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function Delivery({ row }: { row: FeedDelivery }) {
  const [open, setOpen] = useState(false)
  const state = payloadState(row)

  return (
    <li className="rounded-2xl border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 w-full sm:w-auto sm:flex-1">
          {/* Machine-shaped and unbreakable to CSS, so it wraps anywhere. */}
          <p className="text-[14px] font-semibold wrap-anywhere text-ink">
            {row.type}
            {/*
              * Who it was about, beside the trigger (D-831).
              *
              * The row has carried the ids since the first delivery and this
              * screen read none of them, so it said "Taken off the roster,
              * dated 22 Sep 2026" with nobody on it — and the raw body that
              * held the name is dropped after thirty days (D-820), so the row
              * went anonymous exactly when somebody came back to ask what the
              * feed did in September.
              */}
            {row.who ? (
              <span className="font-normal text-ink-2"> &middot; {row.who}</span>
            ) : null}
          </p>

          <p className="mt-0.5 text-[12.5px] wrap-anywhere text-ink-2">{row.note ?? outcomeWords(row.outcome)}</p>

          <p className="mt-1.5 text-[11.5px] wrap-anywhere text-ink-3">
            {new Date(row.receivedAt).toLocaleString()}
            {row.eventOn ? ` · happened ${row.eventOn}` : ''}
            {row.deliveryId ? ` · ${row.deliveryId}` : ''}
          </p>
        </div>

        <Outcome outcome={row.outcome} />
      </div>

      {/*
        <b>Three states, because an empty body is not one thing</b> (D-820). A
        payload dropped by the sweep and a delivery that arrived carrying
        nothing both leave it empty, so the row is marked and this reads the
        mark — guessing would state one as the other, which is D-209 pointed at
        the record itself. It also names the day rather than the window, so
        changing the window cannot make this sentence wrong.
      */}
      {state === 'forgotten' ? (
        <p className="mt-2 text-[11.5px] text-ink-3">
          The message itself was dropped on {new Date(row.bodyForgottenAt!).toLocaleDateString()},
          because it carries a family&rsquo;s own details. What arrived and what was done about it
          are kept.
        </p>
      ) : state === 'never-arrived' ? (
        <p className="mt-2 text-[11.5px] text-ink-3">
          This arrived with nothing in it. There was never a payload to show.
        </p>
      ) : (
        <>
          {/*
            <b>Collapsed, because it is long and it is not what she reads
            first</b> — but one tap away, because it is the only thing on this
            screen that is Jackrabbit's own words rather than ours.
          */}
          <button
            type="button"
            onClick={() => setOpen((was) => !was)}
            className="mt-2 min-h-[32px] text-[12px] font-semibold text-accent-2"
          >
            {open ? 'Hide what was sent' : 'Show what was sent'}
          </button>

          {open && (
            <pre className="mt-2 max-h-80 overflow-auto rounded-lg border border-line bg-surface-2 px-3 py-2 text-[11.5px] text-ink-2">
              {readableBody(row.body)}
            </pre>
          )}
        </>
      )}
    </li>
  )
}

/**
 * **The outcome, in form as well as in words.**
 *
 * The thing being scanned for is *did this change my studio*, and a colour
 * answers that faster than a sentence — with the word still there, because
 * colour alone is not a label.
 *
 * **The words and the colour both live in `api/feed.ts`**, where one test binds
 * them to the C# enum. Written here, a new outcome would render grey and
 * unlabelled and nothing would fail.
 */
function Outcome({ outcome }: { outcome: string }) {
  return (
    <span
      className={`shrink-0 rounded-full border px-3 py-1 text-[11.5px] font-semibold ${outcomeTone(outcome)}`}
    >
      {outcomeWords(outcome)}
    </span>
  )
}
